import PageBannerSlot from '@/components/common/banner/PageBannerSlot';
import SearchResultBanner from '@/components/common/elements/SearchResultBanner';
import PageState from '@/components/common/PageState';
import { ListPageShell, ListTabRow, ListTabs, ListTypeTab, ListFilterRow, ListBody, ListCreateButton, useListHeaderHeight } from '@/components/common/organism/ListPageLayout';
import { FilterChipBar, FilterChipLabel, ListControlRow, SortSelect, listControlGap } from '@/components/common/filter';
import { CONTENT_SORT_DEFAULT, CONTENT_SORT_OPTIONS, ContentSort, parseContentSort } from '@/common/contentSort';
import {useState, useEffect} from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Text from "@/components/common/elements/Text";
import TableList from "@/components/common/organism/TableList";
import PostTable from "@/pages/Post/components/PostTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "@emotion/styled";
import { useAuth, useIsMobile } from '@/common/hooks';
import { POST_SPACE_LABEL, PostSpaceAccess, usePostSpace } from '@/common/postAccess';
import PostSpaceLicenseNotice from './components/PostSpaceLicenseNotice';
import Container from "@/components/common/elements/Container";
import Button from "@/components/common/elements/Button";
import CustomModal from "@/components/common/elements/Modal";
import { api } from '@/api/repository/axios';
import { PostWriteLimit } from '@/common/postWriteLimit';
import WriteLimitModal from '@/pages/Post/components/WriteLimitModal';

import { designSystem } from '@/styles/theme';
import { useListKey, useUiScope } from '@/common/useUiScope';
import { UI_SCREEN } from '@/common/uiEventNames';

interface PostBanner {
    id: number;
    type: 'POST_TOP' | 'POST_BOTTOM';
    title?: string;
    imageUrl: string;
    mobileImageUrl?: string;
    linkUrl?: string;
    displayOrder: number;
}
const FloatingButton = styled.button`
    display: none;

    @media (max-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        bottom: calc(90px + env(safe-area-inset-bottom));
        right: 10px;
        width: 56px;
        height: 56px;
        background-color: ${designSystem.colors.primary};
        color: white;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        font-size: 28px;
        font-weight: 300;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
        transition: all 0.3s ease;
        z-index: 1000;

        &:active {
            transform: scale(0.95);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }
    }
`;

const PageCard = ListPageShell;



const Post = () => {
    const { isLoggedIn } = useAuth();
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchQuery = searchParams.get('search') || '';
    const headerHeight: number = useListHeaderHeight(true);
    const activeSort: ContentSort = parseContentSort(searchParams.get('sort'));
    const changeSort = (next: ContentSort): void => {
        setSearchParams((prev: URLSearchParams) => {
            const params: URLSearchParams = new URLSearchParams(prev);
            if (next === CONTENT_SORT_DEFAULT) params.delete('sort');
            else params.set('sort', next);
            return params;
        }, { replace: true });
    };
    const listUrl: string = (() => {
        const params: URLSearchParams = new URLSearchParams();
        if (searchQuery) params.set('search', searchQuery);
        if (activeSort !== CONTENT_SORT_DEFAULT) params.set('sort', activeSort);
        const qs: string = params.toString();
        return qs ? `/posts?${qs}` : '/posts';
    })();
    /** 검색 띠의 [검색 취소] — 검색어만 빼고 정렬은 둔다 */
    const clearSearch = (): void => {
        setSearchParams((prev: URLSearchParams) => {
            const params: URLSearchParams = new URLSearchParams(prev);
            params.delete('search');
            return params;
        }, { replace: true });
    };
    const sortSelect = (
        <SortSelect options={CONTENT_SORT_OPTIONS} value={activeSort} defaultValue={CONTENT_SORT_DEFAULT} onChange={changeSort} />
    );
    // 지금 보고 있는 공간(치과위생사·치과의사) — 고르는 탭이 아니라 표시용 라벨 하나. 글은 서버가 로그인 회원 공간 것만 준다.
    // 로그인 회원 정보를 읽기 전(null)에는 그리지 않는다(라벨이 바뀌어 보이지 않게)
    const space: PostSpaceAccess | null = usePostSpace();
    // 직군만 치과의사이고 면허 인증 전 — 어느 공간도 아니라 목록 대신 인증 안내(서버도 목록을 403 으로 막는다)
    const licenseRequired: boolean = space === 'LICENSE_REQUIRED';
    const spaceLabel = space && space !== 'LICENSE_REQUIRED' && (
        <FilterChipLabel label={POST_SPACE_LABEL[space]} ariaLabel={`지금 보고 있는 공간: ${POST_SPACE_LABEL[space]}`} />
    );
    const [isOpen, setIsOpen] = useState(false);
    // 노출·클릭 기록 — 검색어·정렬이 바뀌면 새 목록 표시(검색어 원문은 해시로만 listKey 에 들어간다)
    const listKey: string = useListKey(`posts:${space ?? ''}:${searchQuery}:${activeSort}`);
    const listBodyRef = useUiScope({ screen: UI_SCREEN.POST_LIST, section: 'list', listKey });
    const [banners, setBanners] = useState<PostBanner[]>([]);
    const [writeLimit, setWriteLimit] = useState<PostWriteLimit | null>(null);
    const [showWriteLimitModal, setShowWriteLimitModal] = useState(false);

    // 초기 데이터에서 배너 추출
    const handleInitialDataFetched = (result: any) => {
        if (result?.banners) {
            setBanners(result.banners);
        }
    };

    const handleClick = (postId:number) => {
        navigate(`/posts/${postId}`);
    };

    // 글쓰기 진입 전에 도배 방지 제한을 확인한다 — 다 쓰고 나서 막히면 작성한 내용이 헛수고가 된다.
    // 확인 실패(네트워크 등)는 막지 않고 그대로 진입시킨다(fail-open). 등록 시 서버가 한 번 더 막는다.
    const handleWriteClick = async () => {
        if (!isLoggedIn) {
            setIsOpen(true);
            return;
        }
        if (licenseRequired) {
            navigate('/profile/license-verification');
            return;
        }
        try {
            const response = await api.post.writeLimit();
            const limit: PostWriteLimit | undefined = response?.result;
            if (limit?.blocked) {
                setWriteLimit(limit);
                setShowWriteLimitModal(true);
                return;
            }
        } catch (error) {
            console.error('글쓰기 제한 확인 실패:', error);
        }
        navigate('/posts/new');
    };

    const handleLogin = () => {
        navigate('/login');
    };

    const handleBannerClick = (banner: PostBanner) => {
        if (banner.linkUrl) {
            window.open(banner.linkUrl, '_blank');
        }
    };


    return (
        <>
            <>
                <PageCard data-list-page="post">
                    {/* 다른 목록과 같은 머리(탭 줄 + 필터 줄) — PC 만. 폰은 탭·필터 머리 없이 조작 줄(공간 라벨·정렬)만, 글쓰기는 떠 있는 버튼 그대로 */}
                    {!isMobile && (
                        <>
                            <ListTabRow style={{ top: headerHeight }}>
                                <ListTabs>
                                    <ListTypeTab type="button" active aria-pressed>전체</ListTypeTab>
                                </ListTabs>
                                {!licenseRequired && <ListCreateButton type="button" onClick={handleWriteClick}>글쓰기</ListCreateButton>}
                            </ListTabRow>
                            {!licenseRequired && (
                                <ListFilterRow>
                                    <FilterChipBar ariaLabel="속닥속닥 공간·정렬" end={sortSelect}>{spaceLabel}</FilterChipBar>
                                </ListFilterRow>
                            )}
                        </>
                    )}
                    <ListBody ref={listBodyRef}>
                    {isMobile && !licenseRequired && (
                        // 왼쪽 공간 라벨, 오른쪽 정렬. 폰은 위에 탭·필터 줄이 없어 줄이 흰 셸 위 끝에 2px 로 붙고 첫 글 제목도 2px 아래였다 —
                        // 채용 목록(필터 줄 아래 · 첫 카드 위 12px)과 같게 위아래 12px(C6, 2026-10-11). 첫 글 줄은 제목 위 안쪽 여백 11px 을 가진다
                        <ListControlRow style={{ ...listControlGap(1), marginTop: 12 }}>{spaceLabel}{sortSelect}</ListControlRow>
                    )}
                    {licenseRequired ? (
                        <PostSpaceLicenseNotice inList />
                    ) : (
                    <>
                    <PageBannerSlot placement="top" variant="list" />

                    {searchQuery && (
                        <SearchResultBanner keyword={searchQuery} onCancel={clearSearch} />
                    )}

                    <TableList
                        loadingPlacement="page"
                        bottomSpacer={false}
                        key={`${searchQuery}-${activeSort}`}
                        onClick={handleClick}
                        fetchUrl={listUrl}
                        headers={[]}
                        listCount={10}
                        windowSize={5}
                        InnerTable={PostTable}
                        onInitialDataFetched={handleInitialDataFetched}
                        emptyMessage={
                            searchQuery
                                ? <PageState title={`‘${searchQuery}’ 검색 결과가 없어요`} description="다른 낱말로 다시 검색해 보세요." />
                                : <PageState title="아직 게시글이 없어요" description="첫 글을 남겨 보세요. 익명으로 편하게 이야기할 수 있어요." />
                        }
                    />

                    <PageBannerSlot placement="bottom" variant="list" />
                    </>
                    )}
                </ListBody>
                </PageCard>

                {!licenseRequired && (
                    <FloatingButton onClick={handleWriteClick}>
                        <AddRoundedIcon fontSize="inherit" />
                    </FloatingButton>
                )}
            </>

            <CustomModal
                title='게시판'
                width={isMobile ? '90%' : '450px'}
                onClose={() => setIsOpen(false)}
                open={isOpen}
                height='auto'
            >
                <Container width={"100%"} display='block' >
                    <Text textalign='center' size='16px'><strong>로그인후 이용가능합니다.</strong></Text>
                    <Button
                        margin='11px 0 0'
                        onClick={handleLogin}
                        justifycontents='center'
                        color='#fff'
                        bgcolor={designSystem.colors.primary}
                    >
                        로그인
                    </Button>
                </Container>
            </CustomModal>

            <WriteLimitModal
                open={showWriteLimitModal}
                limit={writeLimit}
                onClose={() => setShowWriteLimitModal(false)}
            />
        </>
    );
};

export default Post;
